import apiClient from '../client'
import { buildJsonApiQuery, withoutIncludes } from '@/utils/jsonapi'

/**
 * Integrations API module
 */

export async function fetchIntegrationProviders() {
  const response = await apiClient.get('/integrations/providers/', { params: { include: null } })
  return response.data
}

// buildJsonApiQuery drops `include: null`, and the client interceptor then
// stamps `include=all` - so a caller asking for no includes got every one
// (runs?include=all: 570 kB and ~4 s on Riverbend for data the tab never
// reads). Forward the explicit null so the interceptor honours it.
function listParams(queryOptions) {
  const params = buildJsonApiQuery(queryOptions)
  return queryOptions.include === null ? { ...params, include: null } : params
}

export async function fetchIntegrationConnections(queryOptions = {}) {
  const params = listParams(queryOptions)
  const response = await apiClient.get('/integrations/connections/', { params })
  return response.data
}

/**
 * The read-only starter metric template library (design doc §3): the four
 * canonical measurements (weekend attendance, first-time guests/
 * participation, giving, steps), independent of any Rock/PCO connection.
 * `MeasurementsBoardPage`'s board also carries these per-connection, but
 * that route requires a linked connection - this is the one place a
 * manual-only tenant can see them at all (onboarding audit §ii High 1).
 *
 * @param {Object} [queryOptions] - JSON:API query options (`buildJsonApiQuery` shape)
 * @returns {Promise<Object>} Raw JSON:API response with `metric_template` resources
 */
export async function fetchMetricTemplates(queryOptions = {}) {
  // This is a computed resource with no relationships worth the default
  // `?include=all` round trip (same reasoning as metricsAPI.getUsage).
  const params = withoutIncludes(buildJsonApiQuery(queryOptions))
  const response = await apiClient.get('/integrations/templates/', { params })
  return response.data
}

export async function fetchIntegrationConnection(id) {
  const response = await apiClient.get(`/integrations/connections/${id}/`, { params: { include: null } })
  return response.data
}

export async function createIntegrationConnection(data) {
  const payload = {
    data: {
      type: 'integration_connection',
      attributes: data,
    },
  }
  const response = await apiClient.post('/integrations/connections/', payload)
  return response.data
}

export async function updateIntegrationConnection(id, data) {
  const payload = {
    data: {
      type: 'integration_connection',
      id,
      attributes: data,
    },
  }
  const response = await apiClient.patch(`/integrations/connections/${id}/`, payload)
  return response.data
}

export async function deleteIntegrationConnection(id) {
  await apiClient.delete(`/integrations/connections/${id}/`)
}

export async function startIntegrationAuthorization(id, { frontendRedirectPath } = {}) {
  const body = frontendRedirectPath ? { frontend_redirect_path: frontendRedirectPath } : {}
  const response = await apiClient.post(`/integrations/connections/${id}/authorize/start/`, body, { params: { include: null } })
  return response.data
}

/**
 * Pull `authorization_url` out of an `authorize/start` response, whichever
 * shape the backend answered with (G-4, fast-follow to G-2/G-3):
 *
 * - flat (`_build_rock_begin_authorization`, `renderer_classes=[JSONRenderer]`):
 *   `{authorization_url, mode: "rock_begin", provider, connection_id}`
 * - JSON:API-nested (the OAuth-start branch of the same action):
 *   `{data: {attributes: {authorization_url}}}`
 * - the legacy relationships-nested shape some deployments still answer with:
 *   `{data: {relationships: {data: {data: {attributes: {authorization_url}}}}}}`
 *
 * The flat field is checked first since that's what every current backend
 * build returns; the nested shapes remain as a fallback for older/paired
 * responses. Every call site should read the URL through this helper
 * instead of re-deriving the fallback chain inline.
 *
 * @param {Object} response
 * @returns {string|undefined}
 */
export function authorizationUrlFrom(response) {
  return (
    response?.authorization_url
    ?? response?.data?.attributes?.authorization_url
    ?? response?.data?.relationships?.data?.data?.attributes?.authorization_url
  )
}

export async function runIntegrationHealthCheck(id) {
  const response = await apiClient.post(`/integrations/connections/${id}/health-check/`, null, { params: { include: null } })
  return response.data
}

export async function refreshIntegrationCapabilities(id) {
  const response = await apiClient.post(`/integrations/connections/${id}/capabilities/refresh/`, null, { params: { include: null } })
  return response.data
}

export async function importIntegrationStructure(id) {
  const response = await apiClient.post(`/integrations/connections/${id}/import-structure/`, null, { params: { include: null } })
  return response.data
}

export async function fetchIntegrationSourceOptions(id) {
  const response = await apiClient.get(`/integrations/connections/${id}/source-options/`, { params: { include: null } })
  return response.data
}

export async function bootstrapRockIntegrationAuthorization(data) {
  const response = await apiClient.post('/integrations/oauth/rock/bootstrap/', data || {}, { params: { include: null } })
  return response.data
}

/**
 * Check the Rock address an admin typed before sending them there (the
 * backend's pre-flight; nothing is saved). Resolves to
 * `{authorization_url, mode, provider, base_url, notice}`; a failed check
 * rejects with one of `rockConnectCopy.js`'s codes as `apiError.code`.
 *
 * @param {string} address
 * @returns {Promise<{authorization_url: string, base_url: string, notice: ('linked_elsewhere'|'unverified'|null)}>}
 */
export async function connectRock(address) {
  const response = await apiClient.post('/integrations/rock/connect/', { address }, { params: { include: null } })
  return response.data
}

export async function fetchEmbeddableDashboards() {
  const response = await apiClient.get('/integrations/dashboard-embeds/dashboards/', { params: { include: null } })
  return response.data
}

export async function activateDashboardEmbed(dashboardId, data) {
  const response = await apiClient.post(
    `/integrations/dashboard-embeds/dashboards/${dashboardId}/activate/`,
    data || {},
    { params: { include: null } }
  )
  return response.data
}

export async function revokeDashboardEmbed(dashboardId) {
  const response = await apiClient.post(
    `/integrations/dashboard-embeds/dashboards/${dashboardId}/revoke/`,
    null,
    { params: { include: null } }
  )
  return response.data
}

export async function fetchIntegrationPipelines(queryOptions = {}) {
  const params = listParams(queryOptions)
  const response = await apiClient.get('/integrations/pipelines/', { params })
  return response.data
}

export async function createIntegrationPipeline(data) {
  const payload = {
    data: {
      type: 'integration_pipeline',
      attributes: data.attributes || {},
      relationships: data.relationships || {},
    },
  }
  const response = await apiClient.post('/integrations/pipelines/', payload)
  return response.data
}

export async function updateIntegrationPipeline(id, data) {
  const payload = {
    data: {
      type: 'integration_pipeline',
      id,
      attributes: data.attributes || {},
      relationships: data.relationships || {},
    },
  }
  const response = await apiClient.patch(`/integrations/pipelines/${id}/`, payload)
  return response.data
}

export async function deleteIntegrationPipeline(id) {
  await apiClient.delete(`/integrations/pipelines/${id}/`)
}

export async function validateIntegrationPipeline(id) {
  const response = await apiClient.post(`/integrations/pipelines/${id}/validate/`, null, { params: { include: null } })
  return response.data
}

export async function runIntegrationPipelineNow(id) {
  const response = await apiClient.post(`/integrations/pipelines/${id}/run-now/`, null, { params: { include: null } })
  return response.data
}

export async function fetchIntegrationRun(id) {
  const response = await apiClient.get(`/integrations/runs/${id}/`, { params: { include: null } })
  return response.data
}

export async function fetchIntegrationRuns(queryOptions = {}) {
  const params = listParams(queryOptions)
  const response = await apiClient.get('/integrations/runs/', { params })
  return response.data
}

export async function fetchIntegrationDiagnosticsSummary() {
  const response = await apiClient.get('/integrations/diagnostics/summary/', { params: { include: null } })
  return response.data
}

// ── Metric Data Sources ──

export async function fetchMetricDataSources(metricId) {
  const response = await apiClient.get(`/integrations/metrics/${metricId}/data-sources/`, { params: { include: null } })
  return response.data
}

export async function createMetricDataSource(metricId, data) {
  const response = await apiClient.post(`/integrations/metrics/${metricId}/data-sources/`, data, { params: { include: null } })
  return response.data
}

export async function updateMetricDataSource(metricId, dataSourceId, data) {
  const response = await apiClient.patch(`/integrations/metrics/${metricId}/data-sources/${dataSourceId}/`, data, { params: { include: null } })
  return response.data
}

export async function deleteMetricDataSource(metricId, dataSourceId, { deleteEntries = false } = {}) {
  const params = { include: null }
  if (deleteEntries) params.delete_entries = 'true'
  await apiClient.delete(`/integrations/metrics/${metricId}/data-sources/${dataSourceId}/`, { params })
}

export async function fetchAttendanceLocations(connectionId, eventId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/attendance-locations/${eventId}/`,
    { params: { include: null } }
  )
  return response.data
}

export async function fetchAttendanceTimes(connectionId, eventId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/attendance-times/${eventId}/`,
    { params: { include: null } }
  )
  return response.data
}

export async function previewMetricDataSource(metricId, data) {
  const response = await apiClient.post(
    `/integrations/metrics/${metricId}/data-sources/preview/`,
    data,
    { params: { include: null } }
  )
  return response.data
}

// ── Measurements (Rock) ──

export async function fetchMeasurementsBoard(connectionId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/measurements/board/`,
    { params: { include: null } }
  )
  return response.data
}

export async function fetchCampusMap(connectionId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/measurements/campus-map/`,
    { params: { include: null } }
  )
  return response.data
}

export async function saveCampusMap(connectionId, mappings) {
  const response = await apiClient.post(
    `/integrations/connections/${connectionId}/measurements/campus-map/`,
    { mappings },
    { params: { include: null } }
  )
  return response.data
}

/**
 * "Match all campuses": map every name match and add every missing active
 * Rock campus in Graphit, in one request. Idempotent.
 *
 * @param {string} connectionId
 * @returns {Promise<{rows: Array, results: Array<{rock_campus_id: number, name: string, outcome: 'matched'|'created'|'skipped'|'inactive', reason: string|null}>, summary: {matched: number, created: number, skipped: number, inactive: number}}>}
 */
export async function matchAllCampuses(connectionId) {
  const response = await apiClient.post(
    `/integrations/connections/${connectionId}/measurements/campus-map/match-all/`,
    {},
    { params: { include: null } }
  )
  return response.data
}

/**
 * "Match all service times": match every active Rock schedule to a Graphit
 * service time by name and add the missing ones, in one request. Idempotent.
 *
 * @param {string} connectionId
 * @returns {Promise<{rows: Array, options: Array, results: Array<{external_id: string, name: string, outcome: 'matched'|'created'|'skipped'|'inactive'}>, summary: {matched: number, created: number, skipped: number, inactive: number}}>}
 */
export async function matchAllServiceTimes(connectionId) {
  const response = await apiClient.post(
    `/integrations/connections/${connectionId}/measurements/schedule-map/match-all/`,
    {},
    { params: { include: null } }
  )
  return response.data
}

/**
 * @typedef {Object} OnboardingStatePayload
 * @property {string} connection_id
 * @property {string} onboarding_state
 * @property {string|null} onboarding_updated_at
 * @property {string|null} onboarding_completed_at
 * @property {boolean} onboarding_pending
 * @property {string} setup_path
 * @property {Array<string>} [skipped_metric_ids] - Rock metric ids the person skipped on Discover (addendum A)
 * @property {string|null} [starter_dashboard_id] - set by the server once the starter dashboard is created (addendum A)
 */

const onboardingStatePath = (connectionId) => `/integrations/connections/${connectionId}/onboarding/state/`

/**
 * The guided Rock setup's stored state and choices (T1 + addendum A).
 *
 * @param {string} connectionId
 * @returns {Promise<OnboardingStatePayload>}
 */
export async function fetchOnboardingState(connectionId) {
  const response = await apiClient.get(onboardingStatePath(connectionId), { params: { include: null } })
  return response.data
}

/**
 * Record the guided Rock setup's state (ROCK-ONBOARDING-REWORK T1): the
 * explicit moves no backend action makes, e.g. entering Review or finishing
 * (`done`), and (addendum A) the metrics skipped on Discover. The state is
 * forward only on the server. Administrators only; 400
 * `invalid_onboarding_state` / `not_rock`.
 *
 * @param {string} connectionId
 * @param {string|null} state - one of the backend's `ONBOARDING_STATES`, or null to record only the choices
 * @param {{skippedMetricIds?: Array<string>}} [choices]
 * @returns {Promise<OnboardingStatePayload>}
 */
export async function setOnboardingState(connectionId, state, choices) {
  const body = {
    ...(state ? { state } : {}),
    ...(Array.isArray(choices?.skippedMetricIds) ? { skipped_metric_ids: choices.skippedMetricIds } : {}),
  }
  const response = await apiClient.post(onboardingStatePath(connectionId), body, { params: { include: null } })
  return response.data
}

export async function startMeasurementsSetup(connectionId, items) {
  const response = await apiClient.post(
    `/integrations/connections/${connectionId}/measurements/setup/`,
    { items },
    { params: { include: null } }
  )
  return response.data
}

export async function fetchSetupRun(connectionId, runId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/measurements/setup-runs/${runId}/`,
    { params: { include: null } }
  )
  return response.data
}

export async function fetchExternalMetricPreview(connectionId, externalId, { valueKind = 'actual' } = {}) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/measurements/external-metrics/${externalId}/preview/`,
    { params: { include: null, ...(valueKind === 'goal' ? { value_kind: 'goal' } : {}) } }
  )
  return response.data
}

// MetricBindingViewSet (apps/integrations/views.py, BE-1) exposes a
// read-only /integrations/bindings/ route with
// filterset_fields = {metric, connection, origin, drift}, following the same
// `filter[field]` JSON:API convention as fetchIntegrationConnections /
// fetchIntegrationPipelines above (confirmed by reading BE-1's worktree
// source directly, not inferred - see FE-4-report.md).
export async function fetchMetricBindings(queryOptions = {}) {
  const params = buildJsonApiQuery(queryOptions)
  const response = await apiClient.get('/integrations/bindings/', { params })
  return response.data
}

export async function unlinkRockMetric(metricId) {
  const response = await apiClient.post(`/integrations/metrics/${metricId}/rock/unlink/`, null, {
    params: { include: null },
  })
  return response.data
}

export async function confirmRockMetric(metricId) {
  const response = await apiClient.post(`/integrations/metrics/${metricId}/rock/confirm/`, null, {
    params: { include: null },
  })
  return response.data
}

export async function syncRockMetricNow(metricId) {
  const response = await apiClient.post(`/integrations/metrics/${metricId}/rock/sync-now/`, null, {
    params: { include: null },
  })
  return response.data
}

export async function acceptRockDrift(metricId) {
  const response = await apiClient.post(`/integrations/metrics/${metricId}/rock/accept-drift/`, null, {
    params: { include: null },
  })
  return response.data
}

/** One complete, explicitly paginated page; never includes person profiles. */
export async function fetchConnectionSourceCatalog(connectionId, { catalog, parentId, offset = 0, status = 'all', signal } = {}) {
  const response = await apiClient.get(`/integrations/connections/${connectionId}/source-catalog/`, {
    params: { include: null, catalog, offset, status, ...(parentId ? { parent_id: parentId } : {}) }, signal,
  });
  return response.data;
}


// M-1 (backend, built in parallel - see M-rock-contract.md item 1): one
// family replacing the per-dimension campus-map/schedule-map reads with a
// single "Mappings" view across all four dimensions (campus, schedule,
// group_type, step_type). `campus-map`/`schedule-map` above stay as thin
// aliases, so this is additive, not a replacement of either.
export async function fetchMeasurementsMappings(connectionId) {
  const response = await apiClient.get(
    `/integrations/connections/${connectionId}/measurements/mappings/`,
    { params: { include: null } }
  )
  return response.data
}

export async function saveMeasurementsMappings(connectionId, dimension, mappings) {
  const response = await apiClient.post(
    `/integrations/connections/${connectionId}/measurements/mappings/${dimension}/`,
    { mappings },
    { params: { include: null } }
  )
  return response.data
}

export async function fetchScheduleMap(connectionId, externalId) {
  const response = await apiClient.get(`/integrations/connections/${connectionId}/measurements/schedule-map/`, {
    params: { include: null, external_id: externalId || undefined },
  })
  return response.data
}

export async function saveScheduleMap(connectionId, externalId, mappings) {
  const response = await apiClient.post(`/integrations/connections/${connectionId}/measurements/schedule-map/`, {
    external_id: externalId || null, mappings,
  }, { params: { include: null } })
  return response.data
}
